import type {MaterialWithRecipes} from '@domain/materials'
import type {Asset, OperationMode} from '@hconnect/common/types'
import {KebabMenu} from '@hconnect/uikit/src/lib2'
import {DeleteOutlined, DeleteOutline} from '@mui/icons-material'
import {ListItemSecondaryAction} from '@mui/material'
import React, {useEffect, useState} from 'react'
import {NodeApi} from 'react-arborist'
import {useTranslation} from 'react-i18next'
import {useNavigate} from 'react-router'

import type {Schedule, ScheduleItem} from '../../../common/types'
import {MaterialTreeNode, isExistingMaterialGuard} from '../../types'

import {MaterialDeleteInfo} from './MaterialDeleteInfo'

import {findAllOperationModes} from '@settings/modules/assets'
import {useAssetsQuery, useScheduleQuery} from '@settings/modules/common/hooks'
import {useConfirmDialog} from '@settings/modules/common/providers'
import {CHANGE_MATERIALS_PERMISSION_TYPES} from '@settings/modules/materials/consts'
import {useDeleteMaterial, useMaterialWithRecipesQuery} from '@settings/modules/materials/hooks'
import {useAnyPermission} from '@settings/modules/permissions'
import {PLANT_ROUTES, getUrl, useUrlParam} from '@settings/modules/routing'

interface MaterialsTreeNodeActionsProps {
  node: NodeApi<MaterialTreeNode>
}

interface DeleteMaterialDialogState {
  material: MaterialWithRecipes
  operationModes: OperationMode[]
  scheduleItems: ScheduleItem[]
}

export const MaterialNodeActions: React.FC<MaterialsTreeNodeActionsProps> = ({node}) => {
  const {t} = useTranslation()
  const plantCode = useUrlParam('plantCode')
  const materialType = useUrlParam('materialType')
  const navigate = useNavigate()
  const redirectToMaterialType = () => {
    navigate(
      getUrl(PLANT_ROUTES.MATERIALS.TYPE.MATERIAL_TYPE.path, {
        plantCode,
        materialType
      })
    )
  }
  const canChangeMaterials = useAnyPermission(...CHANGE_MATERIALS_PERMISSION_TYPES)
  const {openDialog} = useConfirmDialog()

  const {mutate: deleteMaterial, isPending: isMaterialDeleting} = useDeleteMaterial()

  const originalEntity = node.data.originalEntity
  const isExistingMaterialNode = isExistingMaterialGuard(originalEntity)
  const selectedMaterialId = isExistingMaterialNode ? Number(node.data.id) : undefined

  const {data: assets} = useAssetsQuery()
  const {data: schedule} = useScheduleQuery()
  const {data: materialWithRecipes} = useMaterialWithRecipesQuery(selectedMaterialId)
  const hasRecipes = (materialWithRecipes?.recipes.length ?? 0) > 0

  const [deleteDialogState, setDeleteDialogState] = useState<DeleteMaterialDialogState | null>(null)
  const [isDeleteConfirmed, setIsDeleteConfirmed] = useState(false)

  const openDeleteMaterialDialog = (
    schedule: Schedule,
    assets: Asset[],
    material: MaterialWithRecipes
  ) => {
    const operationModes = findAllOperationModes(assets, material.recipes)
    const scheduleItems = Object.values(schedule.schedules).filter(
      (item) => operationModes.find((mode) => mode.id === item.assetOperationModeId) !== undefined
    )
    setIsDeleteConfirmed(false)
    setDeleteDialogState({material, operationModes, scheduleItems})
  }

  useEffect(() => {
    if (!deleteDialogState) return
    const {material, operationModes, scheduleItems} = deleteDialogState
    openDialog({
      title: t('materialsSettings.deleteMaterial'),
      maxWidth: 'sm',
      mainAction: {
        color: 'error',
        text: t('common.delete'),
        icon: <DeleteOutline />,
        disabled: !isDeleteConfirmed,
        onAction: () =>
          deleteMaterial({plantCode, materialId: material.id}, {onSuccess: redirectToMaterialType})
      },
      onCancel: () => setDeleteDialogState(null),
      additionalContent: (
        <MaterialDeleteInfo
          material={material}
          operationModes={operationModes}
          scheduleItems={scheduleItems}
          onConfirmationChange={setIsDeleteConfirmed}
        />
      )
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleteDialogState, isDeleteConfirmed])

  return (
    <ListItemSecondaryAction>
      <KebabMenu
        id={`${`settings-tree-item-${node.data.name}`}-menu`}
        actions={
          schedule && assets && materialWithRecipes && !hasRecipes
            ? [
                {
                  icon: <DeleteOutlined fontSize="small" color="error" />,
                  title: t('common.delete'),
                  testId: 'delete_material',
                  isDisabled: isMaterialDeleting || !canChangeMaterials,
                  onClick: () => openDeleteMaterialDialog(schedule, assets, materialWithRecipes)
                }
              ]
            : []
        }
      />
    </ListItemSecondaryAction>
  )
}
